import {
  axiosKeys,
  storeKeys,
  callAPI,
  getStoreState,
  consoleLogs,
  getPushNotificationApi,
} from '@cogitate/ui-multi-utils';
import moment from 'moment/moment';
import NProgress from 'nprogress';
import { toast } from 'react-toastify';
import { commonKeys, DMS_AllowedFiles, ignoreFileView } from 'ui/data/constants/keys';
import { masterModalHelper } from 'ui/redux/actions';
import { currentLineLob, isRoleAgent } from './helper';
import { GET_FILES_LIST_FROM_DMS } from 'ui/redux/action-types/helperActionTypes';
const DocumentDateFormatter = (date) => {
  return `${moment(date).format('L')} ${moment(date).format('h:mm:ss A')}`;
};

const DocumentsColumns = [
  {
    dataField: 'foldername',
    text: 'Folder Name',
    sort: false,
    headerStyle: () => {
      return { width: '13%' };
    },
  },
  {
    dataField: 'username',
    text: 'User',
    sort: false,
    headerStyle: () => {
      return { width: '15%', textAlign: 'center' };
    },
  },
  {
    dataField: 'documenttype',
    text: 'DOCUMENT DESCRIPTION',
    sort: false,
    headerStyle: () => {
      return { width: '13%', textAlign: 'center' };
    },
  },
  {
    dataField: 'filename',
    text: 'FILE NAME',
    sort: false,
    headerStyle: () => {
      return { width: '15%', textAlign: 'center' };
    },
  },
  {
    dataField: 'TransactionDate',
    text: 'Uploaded / Created Date',
    sort: false,
    headerStyle: () => {
      return { width: '20%', textAlign: 'center' };
    },
    customFormatter: DocumentDateFormatter,
  },
  {
    dataField: 'effectivedate',
    text: 'EFFECTIVE DATE',
    sort: false,
    headerStyle: () => {
      return { width: '17%' };
    },
    hidden: true,
  },
  {
    dataField: 'Uri',
    text: 'FileUrl',
    sort: false,
    headerStyle: () => {
      return { width: '20%' };
    },
    hidden: true,
  },
];

const sanitizeFileName = (name) =>
  name
    .replace(/:/g, '')
    .replace(/[<>"/\\|?*]/g, '_')
    .replace(/\s+/g, ' ')
    .trim();

const getDecodedRawFileName = (row) => {
  const rawName = row?.filename || row?.Uri?.split('/').pop() || commonKeys.EmptyString;
  try {
    return decodeURIComponent(rawName);
  } catch {
    return rawName;
  }
};

const getSendDocumentSubject = (summary) => {
  return summary?.PolicyNumber
    ? `VITAL: Document for Policy Number: ${summary.PolicyNumber}`
    : `VITAL: Document for Quote Number: ${summary.QuoteNumber}-${summary.QuoteVersion}`;
};

const isCorrespondenceEmailAttachment = (fileName, row) => {
  if (fileName.toLowerCase().endsWith('.eml')) {
    return true;
  }
  const folder = (row?.foldername || '').toLowerCase();
  return folder === 'correspondence' && /^(AQMk|AAMk)/i.test(fileName);
};

const getAttachmentFileName = (row, summary) => {
  const decodedName = getDecodedRawFileName(row);
  if (isCorrespondenceEmailAttachment(decodedName, row)) {
    return `${sanitizeFileName(getSendDocumentSubject(summary))}.eml`;
  }
  return sanitizeFileName(decodedName);
};

const EmailDocument = async (model, row) => {
  const summary = getStoreState(storeKeys.PolicyReducer);
  NProgress.start();
  let mailBodytext = model.Comments
    ? model.Comments
    : 'Please find attached document for Policy #' +
      (summary.PolicyNumber ? summary.PolicyNumber : summary.QuoteNumber);
  const emailAgent = await getPushNotificationApi(
    summary,
    'sendDocument',
    model.EmailId,
    [],
    [],
    mailBodytext,
    '',
    row.Uri,
    getAttachmentFileName(row, summary)
  );
  if (emailAgent) {
    toast.success('Document Mailed Successfully!');
  } else {
    toast.error('Document Mailed Failed!');
  }
  NProgress.done();
  return;
};

const getFilesListFromDMS = async (requestInput) => {
  try {
    const summary = getStoreState(storeKeys.PolicyReducer);
    const apiBaseURL = `${process.env.NEXT_PUBLIC_DMS_API_BASE_URL}/`;
    const apiRelativeURL = process.env.NEXT_PUBLIC_DMS_API_GET_DOC_LIST_RELATIVE_URL;
    const loggedInUserReducer = getStoreState(storeKeys.LoggedInUserReducer);
    const {
      cloudBlobContainer: containerName = commonKeys.EmptyString,
      carrierCode = commonKeys.EmptyString,
      coverholderCode = commonKeys.EmptyString,
      lobCode = commonKeys.EmptyString,
      stateCode = commonKeys.EmptyString,
      referenceNumber = commonKeys.EmptyString,
    } = requestInput;

    const apiURL = `${apiBaseURL}${apiRelativeURL}?container=${containerName}&blobpath=${carrierCode}${coverholderCode}/${lobCode}/${stateCode}/${referenceNumber}`;
    const headers = {
      'Ocp-Apim-Subscription-Key': process.env.NEXT_PUBLIC_OCP_APIM_SUBSCRIPTION_KEY,
      Authorization: `Bearer ${loggedInUserReducer.encodedJWT}`,
    };
    let policyDocList = [];
    policyDocList = await callAPI(axiosKeys.GetAction, apiURL, null, headers, '');
    const getFilesListFromDMS = await masterModalHelper(GET_FILES_LIST_FROM_DMS, currentLineLob(), [
      summary,
      apiBaseURL,
      apiRelativeURL,
    ]);

    if (getFilesListFromDMS) {
      await Promise.all(getFilesListFromDMS).then((resp) => {
        resp?.forEach((d) => {
          if (d?.length > 0) {
            policyDocList = [...policyDocList.concat(d)];
          }
        });
      });
    }

    if (isRoleAgent(loggedInUserReducer)) {
      policyDocList = policyDocList.filter(
        (doc) => doc.documenttype !== commonKeys.ThirdPartyDocument && doc.documenttype !== commonKeys.RateSheetDocument
      );
    }

    policyDocList = policyDocList.filter((obj) => !obj?.destinationpath?.includes('/Ratesheets/'));

    policyDocList = policyDocList.filter((doc) => {
      // If document has quoteversion property
      if (doc.quoteversion !== undefined && doc.quoteversion !== null) {
        // Only return documents with quoteversion
        return doc.quoteversion === summary?.QuoteVersion;
      } else {
        // Return documents that don't have quoteversion property
        return true;
      }
    });

    const response = policyDocList?.map((obj) => {
      const { transactiondate, ...rest } = obj;
      return {
        TransactionDate: transactiondate,
        ...rest,
        foldername: obj?.foldername ?? obj.transactiontype,
        documenttype: obj?.documenttype ?? obj.transactiontype + ' ' + 'Documents',
      };
    });
    response?.sort((a, b) => {
      return a.foldername?.localeCompare(b.foldername);
    });
    return response;
  } catch (err) {
    console.error(consoleLogs.Utilities.Pages.Application.GetFilesListFromDMS, err);
    throw err;
  }
};

export const getFormData = (policy, file) => {
  const date = moment();
  const loggedInuser = getStoreState(storeKeys.LoggedInUserReducer);

  const infoId = policy.PolicyNumber ? policy.PolicyNumber : policy.QuoteNumber;
  const metadata = {
    PolicyInfoId: infoId,
    PolicyNumber: policy.PolicyNumber,
    CarrierCode: policy.Attributes.Carrier,
    CoverholderCode: policy.Attributes.Coverholder,
    Lob: policy.Attributes.Lob,
    State: policy.Attributes.State,
    TransactionCount: policy.Transaction.Number.toString(),
    TransactionType: policy.Transaction.Type,
    TransactionDate: moment().toDate(),
    UserName: `${loggedInuser.decodedJWT.FirstName} ${loggedInuser.decodedJWT.LastName}`,
    Filename: `${infoId}_${policy.Transaction.Type}_${date}.pdf`,
    Applicationstatus: policy.PolicyStatus,
  };

  metadata.Filename = file?.name !== undefined ? file?.name : `${infoId}_${policy.Transaction.Type}_${date}.pdf`;

  const blobPath = `${policy.Attributes.Carrier}${
    policy.Attributes.Coverholder
  }/${policy.Attributes.Lob}/${policy.Attributes.State}/${policy.PolicyNumber || policy.QuoteNumber}/${metadata.Filename}`;

  return {
    containerName: process.env.NEXT_PUBLIC_DMS_API_NAME,
    blobPath,
    metaData: metadata,
  };
};

export const toastify = (msg, err) => {
  if (err) {
    toast.error(msg);
  } else {
    toast.success(msg);
  }
};

export const commonDocsConfigs = {
  groupColumnName: 'foldername',
  dmsContainerName: 'dms',
  docactionpos: 'center',
  useRefresh: true,
  folderNameOptions: {
    options: [
      { Key: 'Quote', label: 'Quote', value: 'Quote' },
      { Key: 'Quote Proposal', label: 'Quote Proposal', value: 'Quote Proposal' },
      { Key: 'Application', label: 'Application', value: 'Application' },
      { Key: 'Policy', label: 'Policy', value: 'Policy' },
      { Key: 'Cancellation', label: 'Cancellation', value: 'Cancellation' },
      { Key: 'Reinstate', label: 'Reinstate', value: 'Reinstate' },
      { Key: 'Endorsement', label: 'Endorsement', value: 'Endorsement' },
      { Key: 'Renewal', label: 'Renewal', value: 'Renewal' },
    ],
    primaryColor: '#2d75ff',
  },
  tableCols: DocumentsColumns,
  getFormData,
  getFilesListFromDMS,
  EmailDocument: (model, row) => EmailDocument(model, row),
  env: {
    NEXT_PUBLIC_DMS_API_BASE_URL: process.env.NEXT_PUBLIC_DMS_API_BASE_URL,
    NEXT_PUBLIC_DMS_API_AUTH: process.env.NEXT_PUBLIC_DMS_API_AUTH,
    MS_LIVE_APPS_PROXY: process.env.NEXT_PUBLIC_MS_LIVE_APPS_PROXY,
    APIM_SUBSCRIPTION_KEY: process.env.NEXT_PUBLIC_ADAPTIVE_SUBSCRIPTION_KEY,
  },
  fileProps: {
    allowedFiles: DMS_AllowedFiles,
    fileSize: 100 * 1024 * 1024,
    maxNumberOfFiles: 10,
  },
  extras: { uploadDocSize: 'md', uploadDocDialogClassName: 'modal-90w' },
  useNewDocFeature: {
    useMultiDocFeature: true,
    useHiddenFeature: true,
    useDeleteFeature: true,
  },
  useFileViewer: true,
  toast: toastify,
  expandRowProps: { showExpandColumn: true, expandByColumnOnly: false },
  ignoreFileView: ignoreFileView,
  roleBasedFeatures: {
    deleteFeature: ['underwriter'],
    hideFeature: ['underwriter'],
  },
};
